import {
  checkIsMastraErrorToolOutput,
  type MastraErrorToolOutput,
} from "@repo/app-types/agents/mastraErrorToolOutput";
import {
  checkIsNoResultToolOutput,
  type NoResultToolOutput,
} from "@repo/app-types/agents/noResultToolOutput";
import type {
  TestPlaybookNodeToolOutput,
  TestPlaybookOutcomeToolOutput,
} from "@repo/app-types/entities/playbookConfigurationTools";
import { ChatToolBase, type ToolComponent } from "@repo/ui/chat/chatToolCall";
import { buildProposalMeasurementDetail } from "./playbookProposalTool.js";

/**
 * How one test tool describes itself in the transcript. Opening the saved
 * result belongs to the chat's test result list, so the line is provenance
 * only, like the proposal tools'.
 */
interface PlaybookTestToolCopy<Output extends { sample_size?: number }> {
  running: string;
  failed: string;
  title: (output: Output) => string;
}

function buildPlaybookTestTool<Output extends { sample_size?: number }>(
  copy: PlaybookTestToolCopy<Output>,
): ToolComponent<unknown, Output | MastraErrorToolOutput | NoResultToolOutput> {
  return function PlaybookTestTool(props) {
    if (props.status.type === "running") {
      return <ChatToolBase status={props.status}>{copy.running}</ChatToolBase>;
    }

    if (checkIsNoResultToolOutput(props.output)) {
      return (
        <ChatToolBase status={props.status}>
          {props.output.message}
        </ChatToolBase>
      );
    }

    if (checkIsMastraErrorToolOutput(props.output)) {
      return (
        <ChatToolBase status={props.status}>
          An error occurred while {copy.failed}
        </ChatToolBase>
      );
    }

    const output = props.output;
    if (props.status.type !== "complete" || !output) {
      return <ChatToolBase status={props.status}>{copy.running}</ChatToolBase>;
    }

    return (
      <ChatToolBase status={props.status}>
        {copy.title(output)} ·{" "}
        {buildProposalMeasurementDetail(output.sample_size)}
      </ChatToolBase>
    );
  };
}

export const TestPlaybookNodeTool =
  buildPlaybookTestTool<TestPlaybookNodeToolOutput>({
    running: "Testing a playbook node",
    failed: "testing the playbook node",
    title: (output) =>
      `Tested ${output.node_type === "criteria" ? "criteria" : "section"} "${output.node_name}"`,
  });

export const TestPlaybookOutcomeTool =
  buildPlaybookTestTool<TestPlaybookOutcomeToolOutput>({
    running: "Testing the outcome rubric",
    failed: "testing the outcome rubric",
    title: () => "Tested the outcome rubric",
  });
